import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthPanel from '../components/auth/AuthPanel.jsx';
import WelcomeScreen from '../components/onboarding/WelcomeScreen.jsx';
import { apiUrl, appConfig } from '../lib/config.js';
import { supabase } from '../lib/supabase.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useApp } from '../contexts/AppContext.jsx';

const INDUSTRIES = [
  'Tech',
  'Finance',
  'Consulting',
  'Healthcare',
  'Education',
  'Marketing',
  'Operations',
  'Sales',
  'Design',
  'Other',
];

const EXPERIENCE_BUCKETS = [
  { label: '0–2 yrs', value: '0-2' },
  { label: '3–5 yrs', value: '3-5' },
  { label: '6–10 yrs', value: '6-10' },
  { label: '10–20 yrs', value: '10-20' },
  { label: '20+ yrs', value: '20+' },
];

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const TIME_WINDOWS = ['Mornings', 'Afternoons', 'Evenings', 'Weekends OK'];

const EXP_MIDPOINT = {
  '0-2': 1,
  '3-5': 4,
  '6-10': 8,
  '10-20': 15,
  '20+': 22,
};

const STEP_LABELS = ['Sign up', 'Profile', 'Background', 'Availability', 'Resume', 'Welcome'];

function emptyForm(user) {
  return {
    firstName: '',
    lastName: '',
    email: user?.email || '',
    phone: '',
    city: '',
    country: '',
    linkedinUrl: '',
    industry: '',
    currentRole: '',
    experienceBucket: '',
    availableDays: [],
    availableTimes: [],
    videoOk: true,
    resumeFile: null,
    skippedResume: false,
  };
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

function isValidPhone(value) {
  return /^\+?[1-9]\d{6,14}$/.test(String(value || '').replace(/[\s()-]/g, ''));
}

// Canonicalise the user-entered phone before persisting it as contact data.
// Messaging identity columns (`whatsapp_number` / `imessage_number`) are only
// written by the server when Supabase auth has verified the phone number.
function normalizeE164(value) {
  const hadPlus = /\+/.test(String(value || ''));
  let digits = String(value || '').replace(/[^\d]/g, '');
  if (!digits) return '';
  if (!hadPlus && digits.length === 10) digits = `1${digits}`;
  const withPlus = `+${digits}`;
  return /^\+[1-9]\d{6,14}$/.test(withPlus) ? withPlus : String(value || '').trim();
}

function isValidLinkedIn(value) {
  if (!value) return true; // optional
  return /linkedin\.com\/in\//i.test(value);
}

export default function OnboardingFlow() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { interviewee, refreshInterviewee, loading: appLoading } = useApp();

  const [form, setForm] = useState(() => emptyForm(user));
  const [stepIndex, setStepIndex] = useState(0);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Redirect already-onboarded users straight to dashboard.
  useEffect(() => {
    if (authLoading || appLoading) return;
    if (user && interviewee?.career_onboarding_step === 'complete') {
      navigate('/dashboard/jobs', { replace: true });
    }
  }, [authLoading, appLoading, user, interviewee, navigate]);

  // When auth resolves, skip step 1 if already signed in.
  useEffect(() => {
    if (authLoading) return;
    if (user && stepIndex === 0) {
      setStepIndex(1);
    }
  }, [authLoading, user, stepIndex]);

  // Pre-fill email + name when auth or interviewee row appears.
  useEffect(() => {
    if (!user) return;
    setForm((current) => {
      const next = { ...current };
      if (!next.email && user.email) next.email = user.email;
      const metaName = user.user_metadata?.full_name || user.user_metadata?.name;
      if (metaName && !next.firstName && !next.lastName) {
        const [first, ...rest] = String(metaName).trim().split(/\s+/);
        next.firstName = first || '';
        next.lastName = rest.join(' ');
      }
      if (interviewee) {
        next.firstName = next.firstName || (interviewee.full_name?.split(' ')[0] ?? '');
        next.lastName =
          next.lastName ||
          (interviewee.full_name?.split(' ').slice(1).join(' ') ?? '');
        next.phone = next.phone || interviewee.whatsapp_number || interviewee.phone || '';
        next.linkedinUrl = next.linkedinUrl || interviewee.linkedin_url || '';
        next.country = next.country || interviewee.country || '';
        next.industry = next.industry || interviewee.industry || '';
        next.currentRole = next.currentRole || interviewee.desired_role || '';
      }
      return next;
    });
  }, [user, interviewee]);

  const totalSteps = STEP_LABELS.length;
  const stepNumber = stepIndex + 1;

  const canContinue = useMemo(() => {
    if (stepIndex === 0) return Boolean(user);
    if (stepIndex === 1) {
      return (
        form.firstName.trim().length >= 1 &&
        form.lastName.trim().length >= 1 &&
        isValidEmail(form.email) &&
        isValidPhone(form.phone) &&
        form.country.trim().length >= 2 &&
        isValidLinkedIn(form.linkedinUrl)
      );
    }
    if (stepIndex === 2) {
      return (
        Boolean(form.industry) &&
        form.currentRole.trim().length >= 2 &&
        Boolean(form.experienceBucket)
      );
    }
    if (stepIndex === 3) {
      return form.availableDays.length >= 1 && form.availableTimes.length >= 1;
    }
    if (stepIndex === 4) {
      return form.skippedResume || Boolean(form.resumeFile);
    }
    return true;
  }, [stepIndex, user, form]);

  function updateField(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function toggleArrayValue(key, value) {
    setForm((current) => {
      const set = new Set(current[key]);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      return { ...current, [key]: Array.from(set) };
    });
  }

  function goBack() {
    setError('');
    setStepIndex((current) => Math.max(current - 1, user ? 1 : 0));
  }

  async function uploadResume(userId) {
    if (!form.resumeFile || !userId) return null;
    const file = form.resumeFile;
    const path = `${userId}/resume.pdf`;
    const { error: uploadError } = await supabase.storage
      .from('resumes')
      .upload(path, file, { upsert: true, contentType: 'application/pdf' });
    if (uploadError) {
      console.error('Resume upload error', uploadError);
      throw uploadError;
    }
    return path;
  }

  async function persistOnboarding() {
    // Defensive: re-fetch the live session directly from Supabase instead
    // of trusting the AuthContext value. AuthContext can briefly be stale
    // during fast OAuth redirects; the Supabase client keeps the live
    // source of truth in localStorage. If the session has gone stale, try
    // to refresh it before giving up.
    console.log('[persistOnboarding] start; context user =', user?.id ?? null);
    let { data: { session: liveSession } } = await supabase.auth.getSession();
    if (!liveSession) {
      console.warn('[persistOnboarding] getSession returned null — attempting refreshSession');
      const { data: refreshed } = await supabase.auth.refreshSession();
      liveSession = refreshed?.session ?? null;
    }
    if (!liveSession?.user) {
      console.error('[persistOnboarding] still no session after refresh', { liveSession });
      throw new Error('Your sign-in expired. Please sign in again.');
    }
    const liveUser = liveSession.user;
    console.log('[persistOnboarding] proceeding with', { id: liveUser.id, email: liveUser.email });

    let resumePath = null;
    if (form.resumeFile && !form.skippedResume) {
      try {
        resumePath = await uploadResume(liveUser.id);
      } catch (uploadErr) {
        // Continue without resume rather than blocking the user.
        console.warn('Continuing without resume due to upload error', uploadErr);
      }
    }

    const fullName = `${form.firstName.trim()} ${form.lastName.trim()}`.trim();
    const normalizedPhone = normalizeE164(form.phone);

    // Availability prefs + video_ok don't have their own columns on the
    // interviewees table — they live inside the existing JSONB column
    // `career_preferences_json` (added in migration 001).
    const prefs = {
      availability_days: form.availableDays,
      availability_times: form.availableTimes,
      video_ok: Boolean(form.videoOk),
    };

    // The columns the DB actually has (migrations 001 + 010 + 011):
    const dbPayload = {
      user_id: liveUser.id,
      full_name: fullName,
      email: form.email.trim(),
      phone: normalizedPhone,
      linkedin_url: form.linkedinUrl.trim() || null,
      city: form.city.trim() || null,
      country: form.country.trim() || null,
      industry: form.industry || null,
      latest_role: form.currentRole.trim() || null,
      experience_years: String(EXP_MIDPOINT[form.experienceBucket] ?? ''),
      resume_url: resumePath,
      career_preferences_json: prefs,
      career_onboarding_step: 'complete',
      career_status: 'active',
      career_onboarded_at: new Date().toISOString(),
      onboarding_complete: true,
    };

    // The richer payload sent to the server (which can map to columns the
    // client doesn't know about — e.g. unifying first/last name handling).
    const serverPayload = {
      ...dbPayload,
      first_name: form.firstName.trim(),
      last_name: form.lastName.trim(),
      current_role: form.currentRole.trim() || null,
      availability_days: form.availableDays,
      availability_times: form.availableTimes,
      video_ok: Boolean(form.videoOk),
    };

    // The server owns identity/linking semantics. In production, always use it
    // so a verified messaging row can be merged before any auth-owned row is
    // updated via RLS.
    if (appConfig.apiBaseUrl) {
      let response;
      try {
        response = await fetch(apiUrl('/api/v1/onboarding/complete'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${liveSession.access_token}`,
          },
          body: JSON.stringify(serverPayload),
        });
      } catch (fetchError) {
        console.error('Server onboarding fallback threw', fetchError);
        throw new Error(
          `Could not reach onboarding server at ${appConfig.apiBaseUrl}. Check VITE_API_BASE_URL and that the server is running.`,
        );
      }

      if (!response.ok) {
        const detail = await response.text().catch(() => '');
        console.error('Server onboarding fallback failed', response.status, detail);
        throw new Error(
          `Onboarding server returned ${response.status}. ${detail || ''}`.trim(),
        );
      }
    } else {
      // Local fallback for environments without the Express API. This can only
      // update rows already owned by the auth user; RLS intentionally blocks
      // client-side inserts.
      const { error: upsertError } = await supabase
        .from('interviewees')
        .upsert(dbPayload, { onConflict: 'user_id' });

      if (upsertError) {
        console.warn('Direct interviewees upsert blocked and no server endpoint configured', upsertError);
        throw new Error(
          'Could not save your profile: server endpoint not configured. ' +
            'Set VITE_API_BASE_URL on this Vercel project and redeploy.',
        );
      }
    }

    await refreshInterviewee();
  }

  async function goNext() {
    if (!canContinue || saving) return;
    setError('');

    // Final transition: step 5 (Resume) → step 6 (Welcome) writes to DB.
    if (stepIndex === 4) {
      setSaving(true);
      try {
        await persistOnboarding();
        setStepIndex(5);
      } catch (err) {
        console.error('Onboarding persist error', err);
        setError(err?.message || 'Could not save your profile. Please try again.');
      } finally {
        setSaving(false);
      }
      return;
    }

    setStepIndex((current) => Math.min(current + 1, totalSteps - 1));
  }

  function handleResumeChange(event) {
    setError('');
    const file = event.target.files?.[0];
    if (!file) {
      updateField('resumeFile', null);
      return;
    }
    if (file.type !== 'application/pdf') {
      setError('Resume must be a PDF.');
      event.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Resume must be 5 MB or smaller.');
      event.target.value = '';
      return;
    }
    setForm((current) => ({ ...current, resumeFile: file, skippedResume: false }));
  }

  const showBack = stepIndex > (user ? 1 : 0) && stepIndex < totalSteps - 1;
  const isLastStep = stepIndex === totalSteps - 1;

  return (
    <main className="min-h-screen overflow-hidden bg-[radial-gradient(circle_at_top_left,#CCFBF1_0,#F8FAFC_38%,#F1F5F9_100%)] px-4 py-6 text-slate-700 sm:px-6">
      <div className="mx-auto flex w-full max-w-md flex-col">
        <header className="mb-5 rounded-[12px] border border-white/70 bg-white/85 p-4 shadow-sm backdrop-blur">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-bold text-obsidian-400">Pareto Career Agency</p>
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
              Step {stepNumber} of {totalSteps}
            </p>
          </div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200" aria-hidden="true">
            <div
              className="h-full rounded-full bg-teal-600 transition-all duration-300"
              style={{ width: `${(stepNumber / totalSteps) * 100}%` }}
            />
          </div>
          <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500">
            {STEP_LABELS[stepIndex]}
          </p>
        </header>

        <div className="flex-1">
          {stepIndex === 0 ? (
            <StepSignup />
          ) : null}
          {stepIndex === 1 ? (
            <StepProfile
              form={form}
              updateField={updateField}
              googleEmail={Boolean(user?.app_metadata?.provider === 'google')}
            />
          ) : null}
          {stepIndex === 2 ? <StepBackground form={form} updateField={updateField} /> : null}
          {stepIndex === 3 ? (
            <StepAvailability
              form={form}
              updateField={updateField}
              toggleArrayValue={toggleArrayValue}
            />
          ) : null}
          {stepIndex === 4 ? (
            <StepResume form={form} updateField={updateField} onResumeChange={handleResumeChange} />
          ) : null}
          {stepIndex === 5 ? <WelcomeScreen firstName={form.firstName} /> : null}
        </div>

        {error ? (
          <p className="mt-4 rounded-[8px] border border-coral-200 bg-coral-50 px-3 py-2 text-sm text-coral-700">
            {error}
          </p>
        ) : null}

        {!isLastStep ? (
          <footer className="mt-6 flex items-center justify-between gap-3 border-t border-slate-200 pt-4">
            <button
              type="button"
              onClick={goBack}
              disabled={!showBack}
              className="h-12 min-w-24 cursor-pointer rounded-[8px] border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-teal-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Back
            </button>
            <button
              type="button"
              onClick={goNext}
              disabled={!canContinue || saving}
              className="h-12 min-w-36 cursor-pointer rounded-[8px] bg-teal-600 px-5 text-sm font-bold text-white shadow-lg shadow-teal-900/15 transition hover:bg-teal-700 focus:outline-none focus:ring-4 focus:ring-teal-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? 'Saving…' : stepIndex === 4 ? 'Finish' : 'Continue'}
            </button>
          </footer>
        ) : null}
      </div>
    </main>
  );
}

// ─── Step components ───────────────────────────────────────────────

function StepSignup() {
  return (
    <section className="rounded-[16px] border border-white/70 bg-white/95 p-5 shadow-2xl shadow-slate-900/10 backdrop-blur sm:p-7">
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-teal-700">Welcome</p>
      <h2 className="mt-2 font-display text-[28px] leading-[1.1] text-obsidian-400 sm:text-[32px]">
        Create your account
      </h2>
      <p className="mt-2 text-sm leading-6 text-slate-600">
        Get paid for research interviews — and let AI find you a job while you wait.
      </p>
      <div className="mt-5">
        <AuthPanel mode="signup" nextPath="/onboarding" />
      </div>
    </section>
  );
}

function FormLabel({ htmlFor, children, hint }) {
  return (
    <label htmlFor={htmlFor} className="block text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
      {children}
      {hint ? <span className="ml-1 font-normal lowercase tracking-normal text-slate-400">({hint})</span> : null}
    </label>
  );
}

function TextInput({ id, value, onChange, placeholder, type = 'text', readOnly = false, autoComplete }) {
  return (
    <input
      id={id}
      type={type}
      value={value}
      readOnly={readOnly}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      autoComplete={autoComplete}
      className={`mt-1 h-12 w-full rounded-[8px] border border-slate-200 px-4 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-200 ${
        readOnly ? 'cursor-not-allowed bg-slate-50 text-slate-500' : 'bg-white'
      }`}
    />
  );
}

function StepProfile({ form, updateField, googleEmail }) {
  return (
    <section className="rounded-[16px] border border-white/70 bg-white/95 p-5 shadow-2xl shadow-slate-900/10 backdrop-blur sm:p-7">
      <h2 className="font-display text-[26px] leading-tight text-obsidian-400 sm:text-[30px]">
        Profile basics
      </h2>
      <p className="mt-2 text-sm leading-6 text-slate-600">
        We use this to match you with paid interviews and tailor your job applications.
      </p>

      <div className="mt-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <FormLabel htmlFor="firstName">First name</FormLabel>
            <TextInput
              id="firstName"
              value={form.firstName}
              onChange={(value) => updateField('firstName', value)}
              placeholder="Jane"
              autoComplete="given-name"
            />
          </div>
          <div>
            <FormLabel htmlFor="lastName">Last name</FormLabel>
            <TextInput
              id="lastName"
              value={form.lastName}
              onChange={(value) => updateField('lastName', value)}
              placeholder="Doe"
              autoComplete="family-name"
            />
          </div>
        </div>

        <div>
          <FormLabel htmlFor="email">Email</FormLabel>
          <TextInput
            id="email"
            type="email"
            value={form.email}
            onChange={(value) => updateField('email', value)}
            readOnly={googleEmail}
            placeholder="you@example.com"
            autoComplete="email"
          />
        </div>

        <div>
          <FormLabel htmlFor="phone" hint="E.164 format, e.g. +14155551212">Phone</FormLabel>
          <TextInput
            id="phone"
            type="tel"
            value={form.phone}
            onChange={(value) => updateField('phone', value)}
            placeholder="+14155551212"
            autoComplete="tel"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <FormLabel htmlFor="city">City</FormLabel>
            <TextInput
              id="city"
              value={form.city}
              onChange={(value) => updateField('city', value)}
              placeholder="Brooklyn"
              autoComplete="address-level2"
            />
          </div>
          <div>
            <FormLabel htmlFor="country">Country</FormLabel>
            <TextInput
              id="country"
              value={form.country}
              onChange={(value) => updateField('country', value)}
              placeholder="United States"
              autoComplete="country-name"
            />
          </div>
        </div>

        <div>
          <FormLabel htmlFor="linkedinUrl" hint="optional">LinkedIn URL</FormLabel>
          <TextInput
            id="linkedinUrl"
            type="url"
            value={form.linkedinUrl}
            onChange={(value) => updateField('linkedinUrl', value)}
            placeholder="https://www.linkedin.com/in/yourname"
          />
        </div>
      </div>
    </section>
  );
}

function StepBackground({ form, updateField }) {
  return (
    <section className="rounded-[16px] border border-white/70 bg-white/95 p-5 shadow-2xl shadow-slate-900/10 backdrop-blur sm:p-7">
      <h2 className="font-display text-[26px] leading-tight text-obsidian-400 sm:text-[30px]">
        Your background
      </h2>
      <p className="mt-2 text-sm leading-6 text-slate-600">
        Helps us route the right research campaigns and jobs your way.
      </p>

      <div className="mt-5 space-y-4">
        <div>
          <FormLabel htmlFor="industry">Industry</FormLabel>
          <select
            id="industry"
            value={form.industry}
            onChange={(event) => updateField('industry', event.target.value)}
            className="mt-1 h-12 w-full rounded-[8px] border border-slate-200 bg-white px-4 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-200"
          >
            <option value="" disabled>
              Select an industry
            </option>
            {INDUSTRIES.map((industry) => (
              <option key={industry} value={industry}>
                {industry}
              </option>
            ))}
          </select>
        </div>

        <div>
          <FormLabel htmlFor="currentRole">Current or last role</FormLabel>
          <TextInput
            id="currentRole"
            value={form.currentRole}
            onChange={(value) => updateField('currentRole', value)}
            placeholder="Senior Product Designer"
          />
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Years of experience</p>
          <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-5">
            {EXPERIENCE_BUCKETS.map((bucket) => {
              const selected = form.experienceBucket === bucket.value;
              return (
                <button
                  type="button"
                  key={bucket.value}
                  onClick={() => updateField('experienceBucket', bucket.value)}
                  className={`h-12 rounded-[8px] border px-2 text-sm font-bold transition ${
                    selected
                      ? 'border-teal-600 bg-teal-50 text-teal-800 shadow-sm'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {bucket.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function StepAvailability({ form, updateField, toggleArrayValue }) {
  return (
    <section className="rounded-[16px] border border-white/70 bg-white/95 p-5 shadow-2xl shadow-slate-900/10 backdrop-blur sm:p-7">
      <h2 className="font-display text-[26px] leading-tight text-obsidian-400 sm:text-[30px]">
        When can you take calls?
      </h2>
      <p className="mt-2 text-sm leading-6 text-slate-600">
        Paid research interviews are usually 30–60 minutes. Pick all that apply.
      </p>

      <div className="mt-5">
        <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Days</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {DAYS.map((day) => {
            const selected = form.availableDays.includes(day);
            return (
              <button
                type="button"
                key={day}
                onClick={() => toggleArrayValue('availableDays', day)}
                className={`h-11 rounded-full border px-4 text-sm font-bold transition ${
                  selected
                    ? 'border-teal-600 bg-teal-600 text-white shadow-sm'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-5">
        <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Time windows</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {TIME_WINDOWS.map((window) => {
            const selected = form.availableTimes.includes(window);
            return (
              <button
                type="button"
                key={window}
                onClick={() => toggleArrayValue('availableTimes', window)}
                className={`h-11 rounded-full border px-4 text-sm font-bold transition ${
                  selected
                    ? 'border-teal-600 bg-teal-600 text-white shadow-sm'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                {window}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between gap-3 rounded-[8px] border border-slate-200 bg-slate-50 px-4 py-3">
        <div>
          <p className="text-sm font-bold text-obsidian-400">Comfortable on video/audio?</p>
          <p className="mt-1 text-xs text-slate-500">Most paid sessions are recorded.</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={form.videoOk}
          onClick={() => updateField('videoOk', !form.videoOk)}
          className={`relative h-7 w-12 rounded-full transition ${form.videoOk ? 'bg-teal-600' : 'bg-slate-300'}`}
        >
          <span
            className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${
              form.videoOk ? 'left-[22px]' : 'left-0.5'
            }`}
          />
        </button>
      </div>
    </section>
  );
}

function StepResume({ form, updateField, onResumeChange }) {
  return (
    <section className="rounded-[16px] border border-white/70 bg-white/95 p-5 shadow-2xl shadow-slate-900/10 backdrop-blur sm:p-7">
      <h2 className="font-display text-[26px] leading-tight text-obsidian-400 sm:text-[30px]">
        Drop your resume
      </h2>
      <p className="mt-2 text-sm leading-6 text-slate-600">
        Optional but recommended. We use it to tailor every job application and unlock auto-apply.
      </p>

      <label
        htmlFor="resume"
        className="mt-5 flex h-40 cursor-pointer flex-col items-center justify-center gap-2 rounded-[12px] border-2 border-dashed border-slate-200 bg-slate-50 text-center transition hover:border-teal-400 hover:bg-teal-50"
      >
        <p className="font-display text-2xl text-obsidian-400">
          {form.resumeFile ? form.resumeFile.name : 'Tap to upload PDF'}
        </p>
        <p className="text-xs text-slate-500">PDF only, max 5 MB</p>
        <input
          id="resume"
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={onResumeChange}
        />
      </label>

      <button
        type="button"
        onClick={() => {
          updateField('resumeFile', null);
          updateField('skippedResume', true);
        }}
        className={`mt-4 text-sm font-bold ${
          form.skippedResume ? 'text-teal-700' : 'text-slate-500 hover:text-slate-700'
        }`}
      >
        {form.skippedResume ? 'Skipping for now — you can add this later in Profile.' : 'Skip for now'}
      </button>
    </section>
  );
}
